class GoogleReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this._maxReviews = Math.min(parseInt(this.dataset.maxReviews, 10) || 5, 10);
    this._continuousScroll = this.dataset.continuousScroll === 'true';
    this._scrollSpeed = parseFloat(this.dataset.scrollSpeed) || 4;

    this.#buildShell();
    this.#fetchReviews();
  }

  #buildShell() {
    if (this._continuousScroll) {
      this.innerHTML = `
        <div class="gr-wrapper">
          <div class="gr-track gr-track--continuous" role="list"></div>
        </div>
      `;
      return;
    }

    const arrowSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="20" height="20" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>`;

    this.innerHTML = `
      <div class="gr-wrapper">
        <button class="gr-nav gr-nav--prev" aria-label="Föregående recensioner" disabled>${arrowSVG}</button>
        <div class="gr-track" role="list"></div>
        <button class="gr-nav gr-nav--next" aria-label="Nästa recensioner" disabled>${arrowSVG}</button>
      </div>
    `;

    const track = this.querySelector('.gr-track');
    this.querySelector('.gr-nav--prev').addEventListener('click', () => this.#scroll(-1));
    this.querySelector('.gr-nav--next').addEventListener('click', () => this.#scroll(1));
    track.addEventListener('scroll', () => this.#syncNav(), { passive: true });

    this._resizeObserver = new ResizeObserver(() => this.#syncNav());
    this._resizeObserver.observe(track);
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
  }

  async #fetchReviews() {
    const track = this.querySelector('.gr-track');
    track.innerHTML = '<div class="gr-loading" aria-live="polite" aria-label="Laddar recensioner"><span></span><span></span><span></span></div>';

    try {
      const res = await fetch('https://google-reviews-cache.buyn.workers.dev/reviews');

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
      }

      const data = await res.json();
      this.#paint(data.reviews ?? []);
    } catch (err) {
      track.innerHTML = '<p class="gr-notice">Kunde inte ladda recensioner.</p>';
      console.error('[google-reviews]', err);
    }
  }

  #paint(reviews) {
    const track = this.querySelector('.gr-track');
    const items = reviews.slice(0, this._maxReviews);

    if (items.length === 0) {
      track.innerHTML = '<p class="gr-notice">Inga recensioner hittades.</p>';
      return;
    }

    track.innerHTML = items.map(r => this.#cardHTML(r)).join('');

    if (this._continuousScroll) {
      this.#setupContinuousScroll(track);
    } else {
      this.#syncNav();
    }
  }

  #setupContinuousScroll(track) {
    const cards = [...track.children];
    if (cards.length === 0) return;

    const group = document.createElement('div');
    group.className = 'gr-group';
    cards.forEach(card => group.appendChild(card));

    track.innerHTML = '';
    track.appendChild(group);

    // Force layout so we can measure the group width
    const groupWidth = group.offsetWidth;

    // Fill at least 2x the viewport so there's always content visible during the loop
    const copies = Math.ceil((window.innerWidth * 2) / groupWidth) + 1;
    for (let i = 0; i < copies; i++) {
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    // Animate by exactly one group width for a seamless loop
    const duration = this._scrollSpeed * cards.length;
    track.style.setProperty('--gr-duration', `${duration}s`);
    track.style.setProperty('--gr-shift', `${groupWidth}px`);

    this.addEventListener('mouseenter', () => track.classList.add('gr-track--paused'));
    this.addEventListener('mouseleave', () => track.classList.remove('gr-track--paused'));
  }

  #cardHTML(review) {
    const name = this.#esc(review.authorAttribution?.displayName ?? 'Anonym');
    const photoUri = review.authorAttribution?.photoUri;
    const authorUrl = this.#escAttr(review.authorAttribution?.uri ?? '#');
    const rating = review.rating ?? 0;
    const text = review.text?.text ?? '';
    const date = review.publishTime ? this.#fmtDate(review.publishTime) : (review.relativePublishTimeDescription ?? '');

    const avatar = photoUri
      ? `<img src="${this.#escAttr(photoUri)}" alt="" class="gr-avatar gr-avatar--img" width="40" height="40" loading="lazy">`
      : `<span class="gr-avatar gr-avatar--letter" aria-hidden="true" style="--gr-letter-bg:${this.#letterColor(name)}">${name.charAt(0)}</span>`;

    return `
      <a class="gr-card" role="listitem" href="${authorUrl}" target="_blank" rel="noopener noreferrer">
        <div class="gr-card__top">
          ${avatar}
          <div class="gr-card__author">
            <span class="gr-card__name">${name}</span>
            <div class="gr-card__stars" aria-label="${rating} av 5 stjärnor">${this.#starsHTML(rating)}</div>
          </div>
          <span class="gr-google-badge" aria-label="Google-recension">${this.#googleSVG()}</span>
        </div>
        <p class="gr-card__text">${this.#esc(text)}</p>
      </a>
    `;
  }

  #starsHTML(rating) {
    return Array.from({ length: 5 }, (_, i) => {
      const full = i < Math.round(rating);
      return `<svg class="gr-star${full ? ' gr-star--full' : ''}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
    }).join('');
  }

  #googleSVG() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="22" height="22" aria-hidden="true">
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.9c-.5 2.8-2.1 5.1-4.4 6.7v5.5h7.2c4.2-3.9 6.4-9.6 6.4-16.2z"/>
      <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-7.2-5.5c-2 1.3-4.5 2.1-7.3 2.1-5.6 0-10.4-3.8-12.1-8.9H4.5v5.7C8.1 41.9 15.5 46 24 46z"/>
      <path fill="#FBBC05" d="M11.9 28.4c-.4-1.3-.7-2.6-.7-4s.3-2.8.7-4v-5.7H4.5C2.9 17.6 2 20.7 2 24s.9 6.4 2.5 9.3l7.4-4.9z"/>
      <path fill="#EA4335" d="M24 9.5c3.2 0 6 1.1 8.2 3.2l6.1-6.1C34.9 3.2 29.9 1 24 1 15.5 1 8.1 5.1 4.5 11.4l7.4 5.7C13.6 12.3 18.4 9.5 24 9.5z"/>
    </svg>`;
  }

  #scroll(dir) {
    const track = this.querySelector('.gr-track');
    const card = track.querySelector('.gr-card');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
    track.scrollBy({ left: dir * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  #syncNav() {
    const track = this.querySelector('.gr-track');
    const prev = this.querySelector('.gr-nav--prev');
    const next = this.querySelector('.gr-nav--next');
    if (!track || !prev || !next) return;

    const atStart = track.scrollLeft < 4;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;

    prev.disabled = atStart;
    next.disabled = atEnd;
  }

  #fmtDate(iso) {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  #letterColor(name) {
    const colors = ['#4285F4', '#EA4335', '#34A853', '#FBBC05', '#AB47BC', '#00ACC1', '#FF7043', '#5C6BC0'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  #esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #escAttr(str) {
    return String(str).replace(/"/g, '%22').replace(/'/g, '%27');
  }
}

customElements.define('google-reviews-carousel', GoogleReviewsCarousel);
